import { FormField, ResourceType } from "@/types/resource"
import * as z from "zod"
import { toTitleCase } from "@/lib/utils/characters"

export const resourceFieldVisibility = (condition: string, formData: Record<string, any>): boolean => {
  try {
    // Handle && operators by splitting and evaluating each part
    if (condition.includes(" && ")) {
      const parts = condition.split(" && ")
      const result = parts.every((part) => resourceFieldVisibility(part.trim(), formData))
      return result
    }

    // Handle || operators by splitting and evaluating each part
    if (condition.includes(" || ")) {
      const parts = condition.split(" || ")
      const result = parts.some((part) => resourceFieldVisibility(part.trim(), formData))
      return result
    }

    // Handle === operators
    if (condition.includes(" === ")) {
      const parts = condition.split(" === ")
      if (parts.length >= 2) {
        const fieldName = parts[0]?.trim()
        const expectedValue = parts[1]?.trim()
        if (fieldName && expectedValue !== undefined) {
          const actualValue = formData[fieldName]
          const result = actualValue === expectedValue
          return result
        }
      }
    }

    // Handle == operators (fallback)
    if (condition.includes(" == ")) {
      const parts = condition.split(" == ")
      if (parts.length >= 2) {
        const fieldName = parts[0]?.trim()
        const expectedValue = parts[1]?.trim()
        if (fieldName && expectedValue !== undefined) {
          const actualValue = formData[fieldName]
          return actualValue == expectedValue
        }
      }
    }

    // Handle != operators
    if (condition.includes(" != ")) {
      const parts = condition.split(" != ")
      if (parts.length >= 2) {
        const fieldName = parts[0]?.trim()
        const expectedValue = parts[1]?.trim()
        if (fieldName && expectedValue !== undefined) {
          const actualValue = formData[fieldName]
          return actualValue !== expectedValue
        }
      }
    }

    // Handle !== operators
    if (condition.includes(" !== ")) {
      const parts = condition.split(" !== ")
      if (parts.length >= 2) {
        const fieldName = parts[0]?.trim()
        const expectedValue = parts[1]?.trim()
        if (fieldName && expectedValue !== undefined) {
          const actualValue = formData[fieldName]
          return actualValue !== expectedValue
        }
      }
    }

    return true
  } catch (error) {
    console.warn("Error parsing visibility condition:", condition, error)
    return true
  }
}

// Create dynamic Zod schema based on resource fields
export const createResourceSchema = (
  resource: ResourceType | null,
  formData: Record<string, any> = {},
  isEditMode: boolean = false
) => {
  if (!resource || !resource.fields) {
    return z.object({
      name: z.string().min(1, "Name is required"),
      description: z.string().optional(),
    })
  }

  const schemaFields: Record<string, z.ZodTypeAny> = {
    name: z.string().min(1, "Name is required"),
    description: z.string().optional(),
  }

  // Add validation for each field based on its configuration
  const addFieldValidation = (field: FormField) => {
    // Skip validation for container fields (group, flex-row) as they don't have values themselves
    if (field.type === "group" || field.type === "flex-row") {
      if (field.fields) {
        field.fields.forEach(addFieldValidation)
      }
      return
    }

    // Check if field is visible - if not visible, make it optional regardless of required flag
    const isVisible = !field.visible || resourceFieldVisibility(field.visible, formData)
    // Make encrypted fields optional only in edit mode since they're not returned from the API for security
    const shouldValidate = field.required && isVisible && (!field.encrypted || !isEditMode)

    // Handle different field types
    if (field.type === "file") {
      if (shouldValidate) {
        schemaFields[field.name] = z.any().refine(
          (val) => {
            // Check if it's a FileList with files or if it's already processed as base64
            if (val instanceof FileList) {
              return val.length > 0
            }
            // If it's already processed (base64 string), it should be truthy
            return !!val
          },
          {
            message: `${field.label || toTitleCase(field.name)} is required`,
          }
        )
      } else {
        schemaFields[field.name] = z.any().optional()
      }
    } else if (field.type === "number") {
      if (shouldValidate) {
        schemaFields[field.name] = z
          .union([z.string().min(1, `${field.label || toTitleCase(field.name)} is required`), z.number()])
          .refine(
            (val) => {
              const numVal = typeof val === "string" ? Number(val) : val
              return !isNaN(numVal)
            },
            {
              message: `${field.label || toTitleCase(field.name)} must be a valid number`,
            }
          )
      } else {
        schemaFields[field.name] = z.union([z.string(), z.number()]).optional()
      }
    } else if (field.type === "multi-select") {
      if (shouldValidate) {
        schemaFields[field.name] = z.array(z.string()).min(1, `${field.label || toTitleCase(field.name)} is required`)
      } else {
        schemaFields[field.name] = z.array(z.string()).optional()
      }
    } else {
      if (shouldValidate) {
        schemaFields[field.name] = z.string().min(1, `${field.label || toTitleCase(field.name)} is required`)
      } else {
        schemaFields[field.name] = z.string().optional()
      }
    }

    // Add validation for nested fields
    if (field.fields) {
      field.fields.forEach(addFieldValidation)
    }
  }

  resource.fields.forEach(addFieldValidation)

  return z.object(schemaFields)
}

// Utility function to convert file to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const result = reader.result as string
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = result.split(",")[1]
      resolve(base64)
    }
    reader.onerror = (error) => reject(error)
  })
}

// Utility function to clean up form data by removing empty values and non-visible fields
const cleanFormData = (data: any, resource: ResourceType): any => {
  const cleaned: any = {}

  // Helper function to check if a field should be included based on visibility
  const shouldIncludeField = (fieldName: string, fields: FormField[]): boolean | null => {
    for (const field of fields) {
      if (field.name === fieldName) {
        // If field has visibility condition, check if it's visible
        if (field.visible) {
          return resourceFieldVisibility(field.visible, data)
        }
        return true // No visibility condition means always include
      }
      // Check nested fields
      if (field.fields) {
        const result = shouldIncludeField(fieldName, field.fields)
        if (result !== null) {
          return result
        }
      }
    }
    return null // Field not found in this level
  }

  for (const [key, value] of Object.entries(data)) {
    // Skip basic fields that are always needed
    if (key === "name" || key === "description" || key === "type") {
      cleaned[key] = value
      continue
    }

    // Check if this field should be included based on visibility
    if (resource.fields) {
      const shouldInclude = shouldIncludeField(key, resource.fields)

      if (shouldInclude === false) {
        continue // Skip this field as it's not visible
      }
    }

    if (value !== null && value !== undefined && value !== "") {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          cleaned[key] = value
        }
        continue
      }

      if (typeof value === "object" && !(value instanceof FileList)) {
        const cleanedNested = cleanFormData(value, resource)
        if (Object.keys(cleanedNested).length > 0) {
          cleaned[key] = cleanedNested
        }
      } else {
        cleaned[key] = value
      }
    }
  }

  return cleaned
}

export const getDefaultValues = (resource: ResourceType): Record<string, any> => {
  const defaults: Record<string, any> = {
    name: `My ${resource.name}`,
    description: "",
  }

  const processFields = (fields: FormField[]) => {
    fields.forEach((field) => {
      // Skip container fields (group, flex-row) as they don't have values themselves
      if (field.type !== "group" && field.type !== "flex-row") {
        if (field.default !== undefined) {
          defaults[field.name] = field.default
        } else if (field.type === "file") {
          // Initialize file fields as empty FileList
          defaults[field.name] = new DataTransfer().files
        } else if (field.type === "number") {
          // Initialize number fields as strings (since HTML inputs return strings)
          defaults[field.name] = field.default !== undefined ? String(field.default) : ""
        } else if (field.type === "multi-select") {
          defaults[field.name] = Array.isArray(field.default) ? field.default : []
        } else {
          // Initialize other fields as empty strings
          defaults[field.name] = field.default || ""
        }
      }
      if (field.fields) {
        processFields(field.fields)
      }
    })
  }

  if (resource.fields) {
    processFields(resource.fields)
  }

  return defaults
}

// Utility function to process form data and handle file uploads
export const processFormData = async (data: any, resource: ResourceType): Promise<any> => {
  const processedData = { ...data }

  const processFields = async (fields: FormField[]) => {
    for (const field of fields) {
      if (field.type === "file" && processedData[field.name]) {
        const fileList = processedData[field.name] as FileList
        if (fileList && fileList.length > 0) {
          try {
            const file = fileList[0]
            // Validate file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
              throw new Error(`File ${file.name} is too large. Maximum size is 10MB.`)
            }
            const base64 = await fileToBase64(file)
            processedData[field.name] = base64
          } catch (error) {
            console.error(`Error processing file ${field.name}:`, error)
            throw new Error(
              `Failed to process file: ${field.label || field.name}. ${error instanceof Error ? error.message : "Unknown error"}`
            )
          }
        } else if (field.required) {
          throw new Error(`${field.label || toTitleCase(field.name)} is required`)
        }
      } else if (
        field.type === "number" &&
        processedData[field.name] !== undefined &&
        processedData[field.name] !== ""
      ) {
        // Convert string to number for number fields, or keep as number if already a number
        const currentValue = processedData[field.name]
        const numValue = typeof currentValue === "number" ? currentValue : Number(currentValue)
        if (!isNaN(numValue)) {
          processedData[field.name] = numValue
        }
      }

      if (field.fields) {
        await processFields(field.fields)
      }
    }
  }

  if (resource.fields) {
    await processFields(resource.fields)
  }

  // Clean up the data by removing empty values and non-visible fields
  return cleanFormData(processedData, resource)
}
